import { getApiConfig, isApiError } from '@pa/api'
import { getGetClinicQueryKey, getListClinicActivityQueryKey, useUpdateClinic, type ClinicDetail } from '@pa/api/admin'
import { ConfirmDialog, toastApiError } from '@pa/staff'
import { fmtDateTimeOf, Toggle, useToast } from '@pa/ui'
import { useQueryClient } from '@tanstack/react-query'
import { useId, useState, type SyntheticEvent } from 'react'
import { E164_RE, securityCodeResultText, webhookUrl } from '../model/securityCodes'

/** S-04 Connection copy for task 25's card (`securityCodeAuto` / `securityCodePhone` on #41). */
export const SECURITY_CODES_COPY = {
  title: 'Automatic PF security codes',
  label: 'Automatic PF security codes',
  hintOff:
    'Off: when Practice Fusion asks PF Keeper for a security code ("We don\'t recognize this browser"), a person has to finish the login.',
  hintOn:
    'On: PF texts the security code to the Telnyx number below, and PF Keeper enters it by itself. Needs PF Keeper 0.1.4 with login.twoFactor.enabled.',
  notConnected: 'Connect PF Keeper first.',
  confirmTitle: 'Enter Practice Fusion security codes automatically?',
  confirmBody:
    'PF Keeper will enter Practice Fusion security codes automatically: PF texts each code to this clinic\'s Telnyx number, and our server hands it to PF Keeper, which logs in and ticks "Always trust this browser". Every use is recorded in Activity. Use a dedicated PF user for PF Keeper.',
  confirmAction: 'Turn on',
  doneOn: 'Automatic PF security codes turned on',
  doneOff: 'Automatic PF security codes turned off',
  phoneLabel: 'Telnyx number',
  phoneHint: 'The SMS number PF texts the codes to, in E.164 format, e.g. +18475551234. Leave empty to remove it.',
  phoneInvalid: 'Enter the number in E.164 format, e.g. +18475551234.',
  phoneTaken: 'Another clinic already uses this Telnyx number.',
  phoneSaved: 'Telnyx number saved',
  phoneRemoved: 'Telnyx number removed',
  webhookHint: 'In Telnyx, set the Messaging Profile’s inbound webhook (API v2) to',
  lastCode: 'Last code',
  noCode: 'No code received yet',
}

/**
 * S-04 Connection (task 25, D-82): automatic PF security codes via Telnyx SMS. The switch (off by default; turning it
 * on asks for confirmation, off doesn't), the clinic's Telnyx number (E.164; another clinic's → `409 phone_taken`),
 * the webhook URL to set in Telnyx, and the last code's time and result. **The code itself is never shown.**
 */
export function SecurityCodesCard({ clinic }: { clinic: ClinicDetail }) {
  const id = useId()
  const toast = useToast()
  const queryClient = useQueryClient()
  const update = useUpdateClinic()
  const [confirming, setConfirming] = useState(false)
  const [phone, setPhone] = useState<string | null>(null)
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const on = clinic.securityCodeAuto
  const connected = clinic.connection === 'connected'
  const value = phone ?? clinic.securityCodePhone ?? ''
  const status = clinic.securityCodeStatus

  async function patch(data: { securityCodeAuto?: boolean; securityCodePhone?: string | null }, done: string) {
    const updated = await update.mutateAsync({ clinicId: clinic.id, data })
    queryClient.setQueryData(getGetClinicQueryKey(clinic.id), updated)
    void queryClient.invalidateQueries({ queryKey: getListClinicActivityQueryKey(clinic.id) })
    toast(done)
  }

  async function saveSwitch(next: boolean) {
    try {
      await patch({ securityCodeAuto: next }, next ? SECURITY_CODES_COPY.doneOn : SECURITY_CODES_COPY.doneOff)
    } catch (err) {
      toastApiError(toast, err)
    } finally {
      setConfirming(false)
    }
  }

  async function savePhone(e: SyntheticEvent) {
    e.preventDefault()
    const trimmed = value.trim()
    if (trimmed !== '' && !E164_RE.test(trimmed)) {
      setPhoneError(SECURITY_CODES_COPY.phoneInvalid)
      return
    }
    setPhoneError(null)
    try {
      await patch(
        { securityCodePhone: trimmed === '' ? null : trimmed },
        trimmed === '' ? SECURITY_CODES_COPY.phoneRemoved : SECURITY_CODES_COPY.phoneSaved,
      )
      setPhone(null)
    } catch (err) {
      if (isApiError(err) && err.code === 'conflict' && err.details?.reason === 'phone_taken') {
        setPhoneError(SECURITY_CODES_COPY.phoneTaken)
        return
      }
      if (isApiError(err) && err.code === 'invalid_request' && err.issues?.some((i) => i.code === 'phone_invalid')) {
        setPhoneError(SECURITY_CODES_COPY.phoneInvalid)
        return
      }
      toastApiError(toast, err)
    }
  }

  return (
    <section className="card card-pad form-grid" aria-labelledby={`${id}-title`}>
      <h2 className="form-section-title" id={`${id}-title`}>
        {SECURITY_CODES_COPY.title}
      </h2>
      <div className="row testing-switch">
        <Toggle
          checked={on}
          disabled={update.isPending || (!connected && !on)}
          label={SECURITY_CODES_COPY.label}
          onChange={(next) => {
            if (next) setConfirming(true)
            else void saveSwitch(false)
          }}
        />
        <span aria-hidden>{SECURITY_CODES_COPY.label}</span>
      </div>
      <p className="hint">
        {!connected && !on
          ? SECURITY_CODES_COPY.notConnected
          : on
            ? SECURITY_CODES_COPY.hintOn
            : SECURITY_CODES_COPY.hintOff}
      </p>
      <form className="form-grid" onSubmit={(e) => void savePhone(e)} noValidate>
        <div className="field">
          <label className="label" htmlFor={`${id}-phone`}>
            {SECURITY_CODES_COPY.phoneLabel}
          </label>
          <div className="row">
            <input
              id={`${id}-phone`}
              className="input mono"
              type="tel"
              inputMode="tel"
              autoComplete="off"
              placeholder="+18475551234"
              maxLength={16}
              value={value}
              disabled={!connected}
              aria-invalid={phoneError ? true : undefined}
              aria-describedby={phoneError ? `${id}-phone-error` : `${id}-phone-hint`}
              onChange={(e) => {
                setPhone(e.target.value)
                setPhoneError(null)
              }}
            />
            <button type="submit" className="btn btn-secondary" disabled={!connected || update.isPending}>
              Save number
            </button>
          </div>
          {phoneError ? (
            <span className="err-text" id={`${id}-phone-error`}>
              {phoneError}
            </span>
          ) : (
            <span className="hint" id={`${id}-phone-hint`}>
              {SECURITY_CODES_COPY.phoneHint}
            </span>
          )}
        </div>
      </form>
      <p className="hint">
        {SECURITY_CODES_COPY.webhookHint} <span className="mono webhook-url">{webhookUrl(getApiBaseUrl())}</span>
      </p>
      <dl className="kv">
        <dt>{SECURITY_CODES_COPY.lastCode}</dt>
        <dd>
          {status.lastCodeAt === null || status.lastResult === null
            ? SECURITY_CODES_COPY.noCode
            : `${fmtDateTimeOf(status.lastCodeAt, clinic.timeZone)} · ${securityCodeResultText(status.lastResult)}`}
        </dd>
      </dl>
      {confirming && (
        <ConfirmDialog
          title={SECURITY_CODES_COPY.confirmTitle}
          body={SECURITY_CODES_COPY.confirmBody}
          action={SECURITY_CODES_COPY.confirmAction}
          pending={update.isPending}
          onConfirm={() => void saveSwitch(true)}
          onCancel={() => {
            setConfirming(false)
          }}
        />
      )}
    </section>
  )
}

/** The API origin the app talks to (the webhook lives there too); empty when the API isn't configured. */
function getApiBaseUrl(): string {
  try {
    return getApiConfig().baseUrl
  } catch {
    return ''
  }
}
